import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'src/hooks/useTranslationHook';
import {
  useTheme,
  useMediaQuery,
  Box,
  Card,
  CardContent,
  Typography,
  TablePagination,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  FormControlLabel,
  Checkbox,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Toolbar,
} from '@mui/material';
import {
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
  ArrowUpward,
  ArrowDownward,
  UnfoldMore,
} from '@mui/icons-material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { PaginatedGridProps } from './PaginatedGrid.interfaces';
import {
  useMemo,
  useState,
  useEffect,
  ChangeEvent,
  useCallback,
  FC,
} from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  functionalUpdate,
} from '@tanstack/react-table';
import type {
  SortingState,
  ColumnSizingState,
  VisibilityState,
  PaginationState,
  Updater,
} from '@tanstack/react-table';
import { gridColDefToTanStackColumns, createGridRenderCellParams } from './gridColDefToTanStackColumns';
import isAppWithMock from 'src/utils/isAppWithMock';

function applyUpdater<T>(updater: Updater<T>, old: T): T {
  return functionalUpdate(updater, old);
}

interface EmptyGridStateProps {
  dataTestId: string;
}

const EmptyGridState: FC<EmptyGridStateProps> = ({ dataTestId }) => {
  const { translateText } = useTranslation();

  return (
    <Box
      data-testid={`${dataTestId}-empty-state`}
      sx={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        px: 2,
        py: 4,
        textAlign: 'center',
        backgroundColor: 'background.default',
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {translateText('paginatedGrid.emptyTitle')}
      </Typography>
    </Box>
  );
};

interface EditableGridDesktopProps {
  responsiveColumns: GridColDef[] | undefined;
  rows: PaginatedGridProps['rows'];
  loading: boolean;
  sx: PaginatedGridProps['sx'];
  getRowId: PaginatedGridProps['getRowId'];
  onRowClick: PaginatedGridProps['onRowClick'];
  getRowClassName: PaginatedGridProps['getRowClassName'];
  columnVisibilityModel: PaginatedGridProps['columnVisibilityModel'];
  onSortingChange: PaginatedGridProps['onSortingChange'];
  onGlobalFilterChange: PaginatedGridProps['onGlobalFilterChange'];
  serverSide: boolean;
  totalCount: number;
  currentPage: number;
  effectivePageSize: number;
  effectiveRowsPerPageOptions: number[];
  onPageChange: PaginatedGridProps['onPageChange'];
  dataTestId: string;
  dataGridKey: string;
}

function EditableGridDesktop({
  responsiveColumns,
  rows,
  loading,
  sx,
  getRowId,
  onRowClick,
  getRowClassName,
  columnVisibilityModel,
  onSortingChange,
  onGlobalFilterChange,
  serverSide,
  totalCount,
  currentPage,
  effectivePageSize,
  effectiveRowsPerPageOptions,
  onPageChange,
  dataTestId,
  dataGridKey,
}: EditableGridDesktopProps) {
  const { translateText } = useTranslation();
  const dataRows = (rows ?? []) as Record<string, unknown>[];

  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({});
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});
  const [columnMenuAnchor, setColumnMenuAnchor] = useState<null | HTMLElement>(null);

  const derivedVisibility = useMemo(() => {
    if (!columnVisibilityModel) return {};
    const vis: VisibilityState = {};
    Object.entries(columnVisibilityModel).forEach(([field, visible]) => {
      vis[field] = Boolean(visible);
    });
    return vis;
  }, [columnVisibilityModel]);

  useEffect(() => {
    setColumnVisibility(derivedVisibility);
  }, [derivedVisibility]);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: currentPage - 1,
    pageSize: effectivePageSize,
  });

  useEffect(() => {
    if (serverSide) {
      setPagination({
        pageIndex: currentPage - 1,
        pageSize: effectivePageSize,
      });
    }
  }, [currentPage, effectivePageSize, serverSide]);

  const tanstackColumns = useMemo(() => {
    const gridCols = (responsiveColumns ?? []) as GridColDef[];
    return gridColDefToTanStackColumns<Record<string, unknown>>(gridCols, {
      getRowId: getRowId
        ? (row) => {
          const id = getRowId(row as never);
          return id ?? '';
        }
        : undefined,
    });
  }, [responsiveColumns, getRowId]);

  const pageCount = useMemo(() => {
    const count = totalCount ?? dataRows.length;
    const size = pagination.pageSize || 1;
    return Math.max(1, Math.ceil(count / size));
  }, [totalCount, dataRows.length, pagination.pageSize]);

  const handleSortingChange = useCallback(
    (updater: Updater<SortingState>) => {
      setSorting((old) => {
        const next = applyUpdater(updater, old);
        onSortingChange?.(
          next.map((s) => ({
            field: String(s.id),
            direction: s.desc ? 'desc' : 'asc',
          }))
        );
        return next;
      });
    },
    [onSortingChange]
  );

  const handleGlobalFilterChange = useCallback(
    (updater: Updater<string>) => {
      setGlobalFilter((old) => {
        const next = applyUpdater(updater, old);
        onGlobalFilterChange?.(next);
        return next;
      });
    },
    [onGlobalFilterChange]
  );

  const handlePaginationChange = useCallback(
    (updater: Updater<PaginationState>) => {
      setPagination((old) => {
        const next = applyUpdater(updater, old);
        if (serverSide && onPageChange) {
          onPageChange(next.pageIndex + 1, next.pageSize);
        }
        return next;
      });
    },
    [serverSide, onPageChange]
  );

  const table = useReactTable({
    data: dataRows,
    columns: tanstackColumns,
    getRowId: getRowId
      ? (row) => String(getRowId(row))
      : (row) => String((row as { id?: string | number }).id ?? ''),
    state: {
      sorting,
      columnVisibility,
      columnSizing,
      globalFilter,
      pagination,
    },
    onSortingChange: handleSortingChange,
    onGlobalFilterChange: handleGlobalFilterChange,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnSizingChange: setColumnSizing,
    onPaginationChange: handlePaginationChange,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: serverSide ? undefined : getPaginationRowModel(),
    manualPagination: serverSide,
    pageCount: serverSide ? pageCount : undefined,
    globalFilterFn: 'includesString',
    enableColumnResizing: true,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 80,
      maxSize: 2000,
    },
  });

  const filteredCount = table.getFilteredRowModel().rows.length;
  const paginationCount = serverSide ? totalCount || dataRows.length : filteredCount;

  const handleTablePageChange = (_: unknown, newPage: number) => {
    if (serverSide) {
      onPageChange?.(newPage + 1, pagination.pageSize);
    } else {
      table.setPageIndex(newPage);
    }
  };

  const handleTableRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
    const nextSize = parseInt(event.target.value, 10);
    if (serverSide) {
      onPageChange?.(1, nextSize);
    } else {
      table.setPageSize(nextSize);
      table.setPageIndex(0);
    }
  };

  const displayPageIndex = serverSide ? currentPage - 1 : table.getState().pagination.pageIndex;
  const displayPageSize = serverSide ? effectivePageSize : table.getState().pagination.pageSize;
  const visibleRows = table.getRowModel().rows;
  const visibleColumnCount = Math.max(table.getVisibleLeafColumns().length, 1);

  return (
    <Box
      data-testid={dataTestId}
      key={dataGridKey}
      sx={{
        p: 0,
        mt: { xs: 2, sm: 2 },
        mb: { xs: 2, sm: 2 },
        position: 'relative',
        ...sx,
      }}
    >
      <Box
        data-testid={`${dataTestId}-surface`}
        sx={{
          position: 'relative',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        {loading && (
          <LinearProgress
            sx={{ position: 'absolute', top: 0, left: 0, right: 0, zIndex: 5 }}
            data-testid={`${dataTestId}-loading`}
          />
        )}

        <Toolbar
          variant="dense"
          disableGutters
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
            gap: { xs: 1.25, sm: 1 },
            flexWrap: 'wrap',
            minHeight: { xs: 'auto', sm: 48 },
            px: { xs: 1, sm: 1.5 },
            py: { xs: 1, sm: 1 },
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'action.hover',
          }}
          data-testid={`${dataTestId}-toolbar`}
        >
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            useFlexGap
            sx={{ flex: '1 1 auto', minWidth: 0 }}
            data-testid={`${dataTestId}-toolbar-left`}
          >
            <TextField
              size="small"
              value={globalFilter ?? ''}
              onChange={(e) => handleGlobalFilterChange(e.target.value)}
              placeholder={translateText('paginatedGrid.filterPlaceholder')}
              inputProps={{
                'aria-label': translateText('paginatedGrid.globalFilterAria'),
                'data-testid': `${dataTestId}-global-filter`,
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: 0, sm: 200 }, flex: { xs: '1 1 auto', sm: '0 1 240px' } }}
            />
            <IconButton
              aria-label={translateText('paginatedGrid.columnsMenuAria')}
              data-testid={`${dataTestId}-columns-button`}
              onClick={(e) => setColumnMenuAnchor(e.currentTarget)}
              size="small"
            >
              <ViewColumnIcon fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={columnMenuAnchor}
              open={Boolean(columnMenuAnchor)}
              onClose={() => setColumnMenuAnchor(null)}
              data-testid={`${dataTestId}-columns-menu`}
            >
              {table.getAllLeafColumns().map((column) => {
                if (!column.getCanHide()) return null;
                const def = column.columnDef as { meta?: { gridColDef?: GridColDef } };
                const label = String(def.meta?.gridColDef?.headerName ?? column.id);
                return (
                  <Box key={column.id} sx={{ px: 1.5, py: 0.5 }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={column.getIsVisible()}
                          onChange={column.getToggleVisibilityHandler()}
                          data-testid={`${dataTestId}-column-toggle-${column.id}`}
                        />
                      }
                      label={label}
                    />
                  </Box>
                );
              })}
            </Menu>
          </Stack>

          <TablePagination
            component="div"
            count={paginationCount}
            page={displayPageIndex}
            onPageChange={handleTablePageChange}
            rowsPerPage={displayPageSize}
            onRowsPerPageChange={handleTableRowsPerPageChange}
            rowsPerPageOptions={effectiveRowsPerPageOptions}
            labelRowsPerPage={translateText('paginatedGrid.rowsPerPageLabel')}
            sx={{
              flexShrink: 0,
              border: 0,
              m: 0,
              p: 0,
              py: 0,
              alignSelf: { xs: 'stretch', sm: 'center' },
              '& .MuiTablePagination-toolbar': {
                flexWrap: 'wrap',
                justifyContent: { xs: 'center', sm: 'flex-end' },
                minHeight: 40,
                pl: 0,
                pr: 0,
              },
              '& .MuiTablePagination-selectLabel': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-displayedRows': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              },
              '& .MuiTablePagination-select': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              },
              '& .MuiIconButton-root': {
                padding: { xs: '4px', sm: '8px' },
                minWidth: { xs: '44px', sm: '40px' },
                minHeight: { xs: '44px', sm: '40px' },
              },
            }}
            data-testid={`${dataTestId}-pagination`}
          />
        </Toolbar>

        <Box
          className="paginated-grid-scroll"
          sx={{
            maxHeight: 'min(560px, 70vh)',
            overflow: 'auto',
            width: '100%',
            scrollBehavior: 'auto',
            scrollbarGutter: 'stable',
          }}
          data-testid={`${dataTestId}-scroll`}
        >
          <Table
            stickyHeader
            size="small"
            sx={{
              tableLayout: 'fixed',
              width: '100%',
              '& .MuiTableCell-root': {
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
              },
              // Override MUI size="small" padding (12px 16px); use full `padding` + sizeSmall specificity
              '& .MuiTableCell-head.MuiTableCell-sizeSmall': {
                padding: { xs: '4px 4px', sm: '8px 16px' },
              },
              '& .MuiTableCell-body.MuiTableCell-sizeSmall': {
                padding: { xs: '4px 4px', sm: '8px 16px' },
              },
            }}
          >
            <TableHead>
              {table.getHeaderGroups().map((headerGroup) => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, headerIndex) => {
                    const canSort = header.column.getCanSort();
                    const sorted = header.column.getIsSorted();
                    const isLastHeader =
                      headerIndex === headerGroup.headers.length - 1;
                    return (
                      <TableCell
                        key={header.id}
                        colSpan={header.colSpan}
                        data-testid={`${dataTestId}-header-${header.column.id}`}
                        sx={{
                          fontWeight: 'bold',
                          fontSize: { xs: '0.7rem', sm: '0.75rem' },
                          textTransform: 'uppercase',
                          whiteSpace: { xs: 'nowrap', sm: 'normal' },
                          minHeight: { xs: 36, sm: 40 },
                          position: 'sticky',
                          top: 0,
                          zIndex: 2,
                          backgroundColor: 'background.paper',
                          width: header.getSize(),
                          maxWidth: header.getSize(),
                          cursor: canSort ? 'pointer' : 'default',
                          verticalAlign: 'middle',
                          overflow: 'visible',
                          ...(!isLastHeader && {
                            borderRightWidth: 1,
                            borderRightStyle: 'solid',
                            borderRightColor: 'divider',
                          }),
                        }}
                        onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent:
                              header.column.columnDef.meta &&
                                (header.column.columnDef.meta as { align?: string }).align === 'right'
                                ? 'flex-end'
                                : (header.column.columnDef.meta as { align?: string } | undefined)?.align ===
                                  'center'
                                  ? 'center'
                                  : 'flex-start',
                            gap: 0.5,
                            pr: 2,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort &&
                            (sorted === 'asc' ? (
                              <ArrowUpward sx={{ fontSize: 16 }} />
                            ) : sorted === 'desc' ? (
                              <ArrowDownward sx={{ fontSize: 16 }} />
                            ) : (
                              <UnfoldMore sx={{ fontSize: 16, opacity: 0.4 }} />
                            ))}
                        </Box>
                        {/* Overlap cell border + divider so drag hits this layer, not the next column */}
                        <Box
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          onTouchStart={(e) => {
                            e.stopPropagation();
                            header.getResizeHandler()(e);
                          }}
                          sx={{
                            position: 'absolute',
                            right: -6,
                            top: 0,
                            bottom: 0,
                            width: 14,
                            cursor: 'col-resize',
                            userSelect: 'none',
                            touchAction: 'none',
                            zIndex: 4,
                          }}
                        />
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableHead>
            <TableBody>
              {visibleRows.map((row) => {
                const original = row.original;
                const rowId = getRowId
                  ? getRowId(original)
                  : (original as { id?: string | number }).id ?? row.id;
                const classNameExtra = getRowClassName
                  ? getRowClassName({
                    id: rowId,
                    row: original,
                    index: row.index,
                  } as unknown as Parameters<NonNullable<typeof getRowClassName>>[0])
                  : '';
                const isChild = Boolean((original as { isChild?: boolean }).isChild);
                return (
                  <TableRow
                    key={row.id}
                    hover
                    data-paginated-grid-row
                    data-is-child={isChild ? 'true' : 'false'}
                    className={`paginated-grid-row ${classNameExtra ?? ''}`.trim()}
                    sx={{
                      minHeight: { xs: 36, sm: 40 },
                      cursor: onRowClick ? 'pointer' : 'default',
                      '&:hover': {
                        backgroundColor: { xs: 'transparent', sm: 'rgba(0, 0, 0, 0.04)' },
                      },
                    }}
                    onClick={(e) => {
                      if (!onRowClick) return;
                      if ((e.target as HTMLElement).closest('button, a, [role="button"], .MuiIconButton-root')) {
                        return;
                      }
                      try {
                        onRowClick(
                          {
                            row: original,
                            id: rowId,
                          } as never,
                          {} as never,
                          {} as never
                        );
                      } catch (err) {
                        console.warn('onRowClick error', err);
                      }
                    }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell
                        key={cell.id}
                        data-testid={`${dataTestId}-cell-${cell.column.id}`}
                        sx={{
                          minHeight: { xs: 36, sm: 40 },
                          width: cell.column.getSize(),
                          maxWidth: cell.column.getSize(),
                          verticalAlign: 'middle',
                          overflow: 'hidden',
                          '&:focus': { outline: 'none' },
                          '&:focus-within': { outline: 'none' },
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                );
              })}
              {!loading && visibleRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={visibleColumnCount} sx={{ p: '0 !important' }}>
                    <EmptyGridState dataTestId={dataTestId} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>
    </Box>
  );
}

const EditableGrid = ({
  rowsPerPageOptions = [10, 20, 50, 100],
  pageSize = 10,
  dataGridKey = '0',
  dataTestId = 'data-grid',
  serverSide = false,
  totalCount = 0,
  currentPage = 1,
  currentPageSize = 10,
  onPageChange,
  disableRowSelectionOnClick = true,
  hideColumnsOnMobile = [],
  mobilePageSize = 5,
  columns,
  rows,
  loading = false,
  sx,
  getRowId,
  onRowClick,
  getRowClassName,
  columnVisibilityModel,
  onSortingChange,
  onGlobalFilterChange,
  ..._unusedDataGridProps
}: PaginatedGridProps) => {
  void _unusedDataGridProps;
  void disableRowSelectionOnClick;
  const { translateText } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  /** Mock APIs often return full lists; still paginate in the UI (slice / TanStack page model). */
  const isMockUi = isAppWithMock();
  const effectiveServerSide = serverSide && !isMockUi;

  const responsiveColumns = useMemo(() => {
    if (!columns) return columns;

    let processedColumns = columns as GridColDef[];

    if (isMobile && hideColumnsOnMobile.length > 0) {
      processedColumns = processedColumns.filter(
        (col) => !hideColumnsOnMobile.includes(col.field)
      );
    }

    // Cell layout: gridColDefToTanStackColumns only (avoid mutating renderCell — stacked wrappers).
    return processedColumns;
  }, [columns, isMobile, hideColumnsOnMobile]);

  const effectivePageSize = isMobile ? mobilePageSize : currentPageSize;
  const effectiveRowsPerPageOptions = isMobile ? [5, 10, 20] : rowsPerPageOptions;

  const [paginationModel, setPaginationModel] = useState({
    pageSize: effectivePageSize,
    page: currentPage - 1,
  });

  useEffect(() => {
    if (effectiveServerSide) {
      setPaginationModel({
        pageSize: effectivePageSize,
        page: currentPage - 1,
      });
    }
  }, [currentPage, effectivePageSize, effectiveServerSide]);

  const handlePaginationModelChange = (newModel: { pageSize: number; page: number }) => {
    if (effectiveServerSide && onPageChange) {
      onPageChange(newModel.page + 1, newModel.pageSize);
    } else {
      setPaginationModel(newModel);
    }
  };

  const renderMobileCardView = () => {
    if (!rows || !columns) return null;

    const allColumns = columns as GridColDef[];
    const visibleColumns = allColumns.filter(
      (col) => !hideColumnsOnMobile.includes(col.field)
    );

    const actionColumns = visibleColumns.filter(
      (col) => col.field === 'actions' || col.field?.toLowerCase().includes('action')
    );
    const dataColumns = visibleColumns.filter(
      (col) => col.field !== 'actions' && !col.field?.toLowerCase().includes('action')
    );

    const paginatedRows = effectiveServerSide
      ? rows
      : rows.slice(
        paginationModel.page * paginationModel.pageSize,
        paginationModel.page * paginationModel.pageSize + paginationModel.pageSize
      );

    const getCellValue = (row: Record<string, unknown>, column: GridColDef, index: number) => {
      const rawValue = row[column.field];

      if (column.renderCell) {
        try {
          const params = createGridRenderCellParams(row, column, index, getRowId);
          const rendered = column.renderCell(params);
          if (typeof rendered === 'string' || typeof rendered === 'number') {
            return String(rendered);
          }
          return rendered;
        } catch (error) {
          console.warn(`Error in renderCell for ${column.field}:`, error);
        }
      }

      if (rawValue == null) return '';

      if (column.valueFormatter) {
        try {
          const formatter = column.valueFormatter as (p: {
            value: unknown;
            row: Record<string, unknown>;
            field: string;
          }) => unknown;
          const result = formatter({ value: rawValue, row, field: column.field });
          if (result != null && typeof result !== 'object') {
            return String(result);
          }
        } catch {
          // ignore
        }
      }

      if (typeof rawValue === 'object') {
        if (rawValue instanceof Date) {
          try {
            return rawValue.toLocaleDateString();
          } catch {
            return String(rawValue);
          }
        }
        if (Array.isArray(rawValue)) {
          return rawValue.join(', ');
        }
        return '';
      }

      return String(rawValue);
    };

    const handlePageChange = (_event: unknown, newPage: number) => {
      const newModel = { ...paginationModel, page: newPage };
      handlePaginationModelChange(newModel);
    };

    const handleRowsPerPageChange = (event: ChangeEvent<HTMLInputElement>) => {
      const newPageSize = parseInt(event.target.value, 10);
      const newModel = { ...paginationModel, pageSize: newPageSize, page: 0 };
      handlePaginationModelChange(newModel);
    };

    return (
      <Box data-testid={dataTestId}>
        <Stack spacing={2} sx={{ mb: 2 }}>
          {!loading && paginatedRows.length === 0 && (
            <EmptyGridState dataTestId={dataTestId} />
          )}
          {paginatedRows.map((row: Record<string, unknown>, index: number) => {
            const createRowParams = () => ({
              row,
              id: row.id ?? index,
              field: '',
              formattedValue: '',
              value: '',
              colDef: {} as GridRenderCellParams['colDef'],
              api: {} as GridRenderCellParams['api'],
              rowNode: {} as GridRenderCellParams['rowNode'],
              cellMode: 'view' as const,
              hasFocus: false,
              tabIndex: -1,
              getValue: () => null,
            });

            return (
              <Card
                key={String(row.id ?? index)}
                sx={{
                  borderRadius: 2,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  '&:active': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    transform: 'scale(0.98)',
                  },
                }}
                onClick={(e) => {
                  if ((e.target as HTMLElement).closest('.MuiIconButton-root, .MuiChip-root, button')) {
                    return;
                  }
                  if (onRowClick) {
                    try {
                      onRowClick(
                        createRowParams() as never,
                        {} as never,
                        {} as never
                      );
                    } catch (error) {
                      console.warn('Error handling row click:', error);
                    }
                  }
                }}
              >
                <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                  {dataColumns.length > 0 && (
                    <Stack spacing={1.5}>
                      {dataColumns.map((col) => {
                        const cellValue = getCellValue(row, col, index);
                        const label = col.headerName ?? col.field;
                        const isReactElement = typeof cellValue === 'object' && cellValue !== null;

                        return (
                          <Box
                            key={col.field}
                            sx={{
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                              gap: 2,
                              py: 0.5,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                fontSize: '0.65rem',
                                color: 'text.secondary',
                                fontWeight: 600,
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                flexShrink: 0,
                                minWidth: '35%',
                                pt: 0.25,
                              }}
                            >
                              {label}
                            </Typography>
                            {isReactElement ? (
                              <Box
                                sx={{
                                  flex: 1,
                                  textAlign: 'right',
                                  display: 'flex',
                                  justifyContent: 'flex-end',
                                }}
                              >
                                {cellValue}
                              </Box>
                            ) : (
                              <Typography
                                variant="body2"
                                sx={{
                                  fontSize: '0.875rem',
                                  fontWeight: 400,
                                  color: 'text.primary',
                                  lineHeight: 1.5,
                                  textAlign: 'right',
                                  flex: 1,
                                  wordBreak: 'break-word',
                                }}
                              >
                                {String(cellValue || '-')}
                              </Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Stack>
                  )}

                  {actionColumns.length > 0 && (
                    <Box
                      sx={{
                        mt: 2,
                        pt: 2,
                        borderTop: '1px solid',
                        borderColor: 'divider',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                        gap: 1,
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {actionColumns.map((col) => {
                        if (!col.renderCell) return null;
                        try {
                          const params = createGridRenderCellParams(row, col, index, getRowId);
                          return (
                            <Box key={col.field}>{col.renderCell(params)}</Box>
                          );
                        } catch (error) {
                          console.warn(`Error rendering action column ${col.field}:`, error);
                          return null;
                        }
                      })}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </Stack>

        <TablePagination
          component="div"
          count={
            effectiveServerSide ? totalCount ?? rows.length : rows.length
          }
          page={paginationModel.page}
          onPageChange={handlePageChange}
          rowsPerPage={paginationModel.pageSize}
          onRowsPerPageChange={handleRowsPerPageChange}
          rowsPerPageOptions={effectiveRowsPerPageOptions}
          labelRowsPerPage={translateText('paginatedGrid.rowsPerPageLabel')}
          sx={{
            '& .MuiTablePagination-selectLabel': {
              fontSize: '0.8rem',
            },
            '& .MuiTablePagination-displayedRows': {
              fontSize: '0.8rem',
            },
            '& .MuiTablePagination-select': {
              fontSize: '0.8rem',
            },
            '& .MuiIconButton-root': {
              padding: '4px',
              minWidth: '44px',
              minHeight: '44px',
            },
          }}
        />
      </Box>
    );
  };

  return (
    <ErrorBoundary
      FallbackComponent={() => <div>{translateText('common.error')}</div>}
      onError={(error, errorInfo) => {
        console.error('PaginatedGrid error:', error, errorInfo);
      }}
    >
      {isMobile ? (
        renderMobileCardView()
      ) : (
        <EditableGridDesktop
          responsiveColumns={responsiveColumns as GridColDef[] | undefined}
          rows={rows}
          loading={loading}
          sx={sx}
          getRowId={getRowId}
          onRowClick={onRowClick}
          getRowClassName={getRowClassName}
          columnVisibilityModel={columnVisibilityModel}
          onSortingChange={onSortingChange}
          onGlobalFilterChange={onGlobalFilterChange}
          serverSide={effectiveServerSide}
          totalCount={totalCount}
          currentPage={currentPage}
          effectivePageSize={effectivePageSize}
          effectiveRowsPerPageOptions={effectiveRowsPerPageOptions}
          onPageChange={onPageChange}
          dataTestId={dataTestId}
          dataGridKey={dataGridKey}
        />
      )}
    </ErrorBoundary>
  );
};

export default EditableGrid;
